import { useCallback, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { extractSavedCalculationFromPage } from '../shared';
import '../../../ui-styles/ibest-style.css';
import './calcpad.css';
import { db, type SavedPage } from '../shared/database/db';
import { useIndexedDBData, useIndexedDBStats, useSaveToIndexedDB } from '../shared/database/dbHooks';
import { APP_VERSION_POLICIES } from '../shared/appVersionRegistry';
import { generateShareableURL } from '../shared/urlUtils';
import { evaluateAppVersion, type AppVersionEvaluation } from '../shared/versionPolicy';

// Map app names to URL paths
const APP_PATH_MAP: Record<string, string> = {
  iSteelElement: '/iSteelElement/',
  iSDOFSolver: '/iSDOFsolver/',
  iConcreteElement: '/iConcrete/',
  iCMUWall: '/iCMUWall/',
  iSteelPlate: '/iSteelPlate/',
  iMetalStud: '/iMetalStud/',
  iMetalDeck: '/iMetalDeck/',
  iQuickCalc: '/iQuickCalc/',
  iPIDiagram: '/iPIDiagram/',
  iVCEBlast: '/iVCEBlast/',
  iKRDiagram: '/iKRDiagram/',
  iCPV: '/iCPV/',
  iSecondaryFragments: '/iSecondaryFragments/',
  iVentedBlast: '/iVentedBlast/',
  iCubicle: '/iCubicle'
};

export default function App() {
  const [selectedApp, setSelectedApp] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<SavedPage | null>(null);
  const [activeTab, setActiveTab] = useState<'inputs' | 'results' | 'charts'>('inputs');
  const [ioStatus, setIoStatus] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const allData = useIndexedDBData();
  const stats = useIndexedDBStats();
  const { deleteData } = useSaveToIndexedDB();

  // Get unique apps and projects
  const apps = useMemo(() => {
    const unique = [...new Set(allData.map(item => item.appName))];
    return unique.sort();
  }, [allData]);

  const projects = useMemo(() => {
    const unique = [...new Set(allData.map(item => item.project))];
    return unique.sort();
  }, [allData]);

  // Filtered data
  const filteredData = useMemo(() => {
    return allData.filter(item => {
      const matchesApp = selectedApp === 'all' || item.appName === selectedApp;
      const matchesProject = selectedProject === 'all' || item.project === selectedProject;
      const matchesSearch = !searchTerm || item.title.toLowerCase().includes(searchTerm.toLowerCase()) || item.appName.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesApp && matchesProject && matchesSearch;
    });
  }, [allData, selectedApp, selectedProject, searchTerm]);

  const versionEvaluations = useMemo(() => {
    return allData.reduce<Record<number, AppVersionEvaluation>>((acc, item) => {
      if (!item.id) return acc;

      const policy = APP_VERSION_POLICIES[item.appName];
      if (!policy) return acc;

      const evaluation = evaluateAppVersion(item.metadata?.version, policy);
      if (evaluation.severity !== 'none') {
        acc[item.id] = evaluation;
      }

      return acc;
    }, {});
  }, [allData]);

  const rerunRequiredCount = useMemo(() => Object.values(versionEvaluations).filter(evaluation => evaluation.severity === 'required').length, [versionEvaluations]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      await deleteData(id);
      if (selectedItem?.id === id) {
        setSelectedItem(null);
      }
    }
  };

  const handleOpen = (item: SavedPage) => {
    // Generate short URL with just calcId for CalcPad opens
    const pathname = APP_PATH_MAP[item.appName] || '/';
    const url = `${window.location.origin}${pathname}?calcId=${item.id}`;

    window.open(url, '_blank');
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const selectedCalculation = useMemo(() => extractSavedCalculationFromPage<Record<string, unknown>, Record<string, unknown>>(selectedItem), [selectedItem]);
  const selectedInputs = selectedCalculation?.inputs ?? null;
  const selectedResults = selectedCalculation?.results;
  const selectedPath = selectedItem ? APP_PATH_MAP[selectedItem.appName] || '/' : '/';
  const shareableUrl = selectedInputs ? generateShareableURL(selectedInputs, selectedPath) : '';
  const chartEntries = selectedResults?.charts && typeof selectedResults.charts === 'object' ? Object.entries(selectedResults.charts as Record<string, { mime: string; base64: string }>).filter(([, value]) => value?.base64) : [];
  const hasCharts = chartEntries.length > 0;
  const selectedVersionEvaluation = selectedItem?.id ? versionEvaluations[selectedItem.id] : undefined;

  const downloadBackup = useCallback((items: SavedPage[], scope: 'all' | 'filtered') => {
    const backup = {
      format: 'iBestCalcPadBackup',
      version: 1,
      exportedAt: Date.now(),
      scope,
      count: items.length,
      items: items.map(item => ({
        appName: item.appName,
        project: item.project,
        url: item.url,
        timestamp: item.timestamp,
        title: item.title,
        data: item.data,
        metadata: item.metadata ?? undefined
      }))
    };

    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `icalcpad-backup-${scope}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setIoStatus(`Exported ${items.length} record(s) to JSON.`);
  }, []);

  const handleExportAll = useCallback(() => {
    downloadBackup(allData, 'all');
  }, [allData, downloadBackup]);

  const handleExportFiltered = useCallback(() => {
    downloadBackup(filteredData, 'filtered');
  }, [downloadBackup, filteredData]);

  const isSavedPageLike = (item: unknown): item is Omit<SavedPage, 'id'> => {
    if (!item || typeof item !== 'object') return false;
    const candidate = item as Record<string, unknown>;
    return typeof candidate.appName === 'string' && typeof candidate.project === 'string' && typeof candidate.title === 'string' && typeof candidate.data === 'object' && candidate.data !== null;
  };

  const handleImportFile = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as unknown;

      const sourceItems = Array.isArray(parsed) ? parsed : ((parsed as { items?: unknown[] })?.items ?? []);

      if (!Array.isArray(sourceItems)) {
        throw new Error('Invalid backup format: expected array or { items: [] }.');
      }

      const validItems = sourceItems.filter(isSavedPageLike);
      if (validItems.length === 0) {
        throw new Error('No valid records found in file.');
      }

      const records: Omit<SavedPage, 'id'>[] = validItems.map(item => ({
        appName: item.appName,
        project: item.project,
        title: item.title,
        data: item.data,
        url: typeof item.url === 'string' ? item.url : '',
        timestamp: typeof item.timestamp === 'number' ? item.timestamp : Date.now(),
        metadata: item.metadata
      }));

      await db.savedPages.bulkAdd(records);
      setIoStatus(`Imported ${records.length} record(s) from ${file.name}.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Import failed.';
      setIoStatus(`Import error: ${message}`);
    } finally {
      event.target.value = '';
    }
  }, []);

  const openImportPicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const statusClass = ioStatus.startsWith('Import error') ? 'calcpad__status--error' : 'calcpad__status--ok';

  return (
    <div className="calcpad h-screen w-screen flex flex-col">
      <div className="calcpad__header px-4 md:px-6 py-4 md:py-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <p className="calcpad__eyebrow">OpenIntegral Workspace</p>
            <h1 className="calcpad__title">iCalcPad Data Manager</h1>
            <p className="calcpad__subtitle">Archive, restore, and inspect engineering runs across all apps.</p>
            <div className="calcpad__stats mt-3">
              <span className="calcpad__stat">Total {stats?.total ?? 0}</span>
              <span className="calcpad__stat">Apps {Object.keys(stats?.byApp ?? {}).length}</span>
              <span className="calcpad__stat">Projects {Object.keys(stats?.byProject ?? {}).length}</span>
              {rerunRequiredCount > 0 && <span className="calcpad__stat calcpad__stat--warning">Rerun {rerunRequiredCount}</span>}
            </div>
            {ioStatus ? <p className={`calcpad__status mt-2 ${statusClass}`}>{ioStatus}</p> : null}
          </div>

          <div className="calcpad__actions">
            <button onClick={handleExportFiltered} className="calcpad__action-btn" title="Export filtered list">
              Export Filtered JSON
            </button>
            <button onClick={handleExportAll} className="calcpad__action-btn" title="Export all saved items">
              Export All JSON
            </button>
            <button onClick={openImportPicker} className="calcpad__action-btn calcpad__action-btn--primary" title="Import backup JSON">
              Import JSON
            </button>
            <input ref={fileInputRef} type="file" accept="application/json,.json" onChange={handleImportFile} className="hidden" />
          </div>
        </div>

        <div className="calcpad__filters mt-4">
          <div className="calcpad__field">
            <label className="calcpad__label">App</label>
            <select value={selectedApp} onChange={e => setSelectedApp(e.target.value)} className="calcpad__control">
              <option value="all">All Apps</option>
              {apps.map(app => (
                <option key={app} value={app}>
                  {app}
                </option>
              ))}
            </select>
          </div>

          <div className="calcpad__field">
            <label className="calcpad__label">Project</label>
            <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} className="calcpad__control">
              <option value="all">All Projects</option>
              {projects.map(project => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
          </div>

          {(selectedApp !== 'all' || selectedProject !== 'all' || searchTerm) && (
            <button
              onClick={() => {
                setSelectedApp('all');
                setSelectedProject('all');
                setSearchTerm('');
              }}
              className="calcpad__clear-btn"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-hidden px-4 md:px-6 pb-4 md:pb-6">
        <div className="calcpad__workspace h-full">
          <div className="calcpad__list-pane">
            <div className="calcpad__pane-header">
              <h2 className="calcpad__pane-title">Saved Runs</h2>
              <p className="calcpad__pane-subtitle">Showing {filteredData.length} item(s)</p>
            </div>

            {filteredData.length === 0 ? (
              <div className="calcpad__empty">No items found for this filter set.</div>
            ) : (
              <div className="calcpad__list">
                {filteredData.map(item => {
                  const versionEvaluation = item.id ? versionEvaluations[item.id] : undefined;

                  return (
                    <div
                      key={item.id}
                      className={`calcpad__item ${selectedItem?.id === item.id ? 'calcpad__item--active' : ''} ${versionEvaluation?.severity === 'required' ? 'calcpad__item--stale' : ''}`}
                      onClick={() => {
                        setSelectedItem(item);
                        setActiveTab('inputs');
                      }}
                    >
                      <div className="calcpad__item-main">
                        <div className="calcpad__item-title-row">
                          <h3 className="calcpad__item-title">{item.title}</h3>
                          {versionEvaluation && <span className={`calcpad__version-badge calcpad__version-badge--${versionEvaluation.severity}`}>{versionEvaluation.severity === 'required' ? 'Rerun required' : 'Rerun recommended'}</span>}
                        </div>
                        <p className="calcpad__item-meta">
                          {item.appName} · {item.project}
                        </p>
                        <p className="calcpad__item-time">{formatDate(item.timestamp)}</p>
                      </div>
                      <div className="calcpad__item-actions">
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleOpen(item);
                          }}
                          className="calcpad__mini-btn"
                          title="Open in new tab with inputs"
                        >
                          Open
                        </button>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleDelete(item.id!);
                          }}
                          className="calcpad__mini-btn calcpad__mini-btn--danger"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="calcpad__detail-pane">
            {selectedItem ? (
              <div className="calcpad__detail-inner">
                <div className="calcpad__detail-head">
                  <h2 className="calcpad__detail-title">{selectedItem.title}</h2>
                  <div className="calcpad__detail-grid">
                    <div>
                      <span>App</span>
                      <strong>{selectedItem.appName}</strong>
                    </div>
                    <div>
                      <span>Project</span>
                      <strong>{selectedItem.project}</strong>
                    </div>
                    <div>
                      <span>Date</span>
                      <strong>{formatDate(selectedItem.timestamp)}</strong>
                    </div>
                    <div>
                      <span>Version</span>
                      <strong>{selectedItem.metadata?.version ?? 'N/A'}</strong>
                    </div>
                  </div>
                  {selectedItem.metadata?.notes && <p className="calcpad__notes">Notes: {selectedItem.metadata.notes}</p>}
                  {selectedVersionEvaluation && (
                    <div className={`calcpad__version-alert calcpad__version-alert--${selectedVersionEvaluation.severity}`}>
                      <strong>{selectedVersionEvaluation.severity === 'required' ? 'Re-run required' : 'Re-run recommended'}</strong>
                      <span>
                        Saved with {selectedVersionEvaluation.savedVersion ?? 'unknown version'}; current version is {selectedVersionEvaluation.currentVersion}. {selectedVersionEvaluation.message}
                      </span>
                    </div>
                  )}
                </div>

                <div className="calcpad__share">
                  <div className="calcpad__share-label">Shareable Link</div>
                  <div className="calcpad__share-row">
                    <input type="text" readOnly value={shareableUrl} className="calcpad__share-input" onClick={e => e.currentTarget.select()} />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(shareableUrl);
                      }}
                      className="calcpad__action-btn calcpad__action-btn--primary"
                      title="Copy to clipboard"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div className="calcpad__tabs">
                  <button onClick={() => setActiveTab('inputs')} className={`calcpad__tab ${activeTab === 'inputs' ? 'calcpad__tab--active' : ''}`}>
                    Inputs
                  </button>
                  <button onClick={() => setActiveTab('results')} className={`calcpad__tab ${activeTab === 'results' ? 'calcpad__tab--active' : ''}`}>
                    Results
                  </button>
                  {hasCharts && (
                    <button onClick={() => setActiveTab('charts')} className={`calcpad__tab ${activeTab === 'charts' ? 'calcpad__tab--active' : ''}`}>
                      Charts
                    </button>
                  )}
                </div>

                {activeTab !== 'charts' && (
                  <div className="calcpad__json">
                    <pre>{activeTab === 'inputs' ? JSON.stringify(selectedInputs ?? selectedItem.data, null, 2) : JSON.stringify(selectedResults ?? 'No results available', null, 2)}</pre>
                  </div>
                )}
                {activeTab === 'charts' && (
                  <div className="calcpad__charts-grid">
                    {chartEntries.map(([key, chart]) => (
                      <div key={key} className="calcpad__chart-item">
                        <p className="calcpad__chart-label">{key}</p>
                        <img src={`data:${chart.mime};base64,${chart.base64}`} alt={key} className="calcpad__chart-img" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="calcpad__empty-detail">
                <p>Select a saved run to inspect its payload.</p>
                <p>Use filters to narrow down records quickly.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
